import React, { useState, useCallback } from "react"
import { Form, Alert } from "react-bootstrap"
import OTPDialog from "../containers/Auth/OTPDialog"
import usePasswordConfirmation from "./usePasswordConfirmation"
import useUXStore from "../api/ux/store"
import { useUser } from "../api/users/store"
import { requestForceDeleteOtp, ForceDeleteError, OwnerConstraint } from "../api/users/actions"

// Force deleting a user is irreversible and reaches across every company, so it is
// deliberately a three-step flow: confirm the target by typing their email, re-enter
// the operator's own password, then a one-time code emailed to them.
//
// The password is exchanged for a short-lived token in step two. Step three sends only
// that token, so the password never has to be held in component state while the code
// dialog is open, and a resend can reuse the token.

const dialogId = "force-delete-user"

// Turns a server error code into something an operator can act on.
export function forceDeleteMessage(err: ForceDeleteError): string {
    switch (err?.code) {
        case "force_delete_last_owner": {
            const coms = (err.blocking || []).map((b: OwnerConstraint) => b.com).join(", ")
            return coms
                ? `This user is the only owner of ${coms}. Give those companies another owner first.`
                : "This user is the only owner of a company."
        }
        case "core1_owner_protected":
            return "CoreONE owners cannot be force deleted."
        case "force_delete_self":
            return "You cannot force delete your own account."
        case "unauthorized":
            return "You do not have permission to force delete this user."
        case "user_not_found":
            return "That user no longer exists."
        case "invalid_otp":
            return "That code is incorrect or has expired."
        case "invalid_token":
            return "This request expired. Please start again."
        case "rate_limit_exceeded":
            return "Too many attempts. Wait a few minutes and try again."
        case "pass_invalid":
            return "That password is incorrect."
        default:
            return "Could not delete this user. Please try again."
    }
}

/**
 * useForceDeleteUser drives the core1 force-delete flow.
 *
 * Call start(email) to begin. Render the returned node somewhere in the tree so the
 * one-time code dialog has a mount point.
 */
const useForceDeleteUser = () => {
    const { refreshDialog, hideDialog, addToast } = useUXStore(state => state)
    const { confirm: confirmPassword } = usePasswordConfirmation()
    const forceDelete = useUser(state => state.forceDelete)

    const [target, setTarget] = useState<string | null>(null)
    const [token, setToken] = useState<string | null>(null)

    const reset = useCallback(() => {
        setTarget(null)
        setToken(null)
    }, [])

    // Step 3 - the code, then the purge. OTPDialog surfaces whatever is thrown.
    const handleVerify = useCallback(async (otp: string) => {
        if (!token) throw new Error("This request expired. Please start again.")
        try {
            await forceDelete(token, otp)
        } catch (err) {
            throw new Error(forceDeleteMessage(err as ForceDeleteError))
        }
        reset()
    }, [token, forceDelete, reset])

    const handleResend = useCallback(async () => {
        if (!target) throw new Error("This request expired. Please start again.")
        try {
            const next = await requestForceDeleteOtp(target, undefined, token || undefined)
            setToken(next)
        } catch (err) {
            throw new Error(forceDeleteMessage(err as ForceDeleteError))
        }
    }, [target, token])

    // Step 2 - the operator's own password, exchanged for a token.
    const askPassword = useCallback((email: string) => {
        confirmPassword(async (password: string) => {
            try {
                const issued = await requestForceDeleteOtp(email, password)
                setTarget(email)
                setToken(issued)
            } catch (err) {
                // Rethrown so the password dialog renders it inline.
                throw new Error(forceDeleteMessage(err as ForceDeleteError))
            }
        }, {
            title: "Confirm your password",
            message: `Re-enter your password to permanently delete ${email}.`
        })
    }, [confirmPassword])

    // Step 1 - make the operator type the target out, so a misclick cannot destroy an
    // account, and spell out exactly what is about to happen.
    const start = useCallback((email: string) => {
        let typed = ""

        const render = (error: string | null) => {
            refreshDialog({
                id: dialogId,
                title: "Force delete user",
                visible: true,
                confirmContent: "Continue",
                content: (
                    <div>
                        <Alert variant="danger">
                            This permanently deletes <b>{email}</b> and everything belonging to
                            them, in <b>every company</b>: access and invitations, sign-in
                            sessions, MFA PIN and passkeys, registered devices, branches,
                            preferences, notifications, stored files and their security history.
                            <br /><br />
                            This cannot be undone.
                        </Alert>
                        <Form.Label>Type <b>{email}</b> to confirm</Form.Label>
                        <Form.Control
                            autoFocus
                            defaultValue={typed}
                            placeholder={email}
                            onChange={(e) => { typed = e.target.value }}
                        />
                        {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
                    </div>
                ),
                onClose: () => hideDialog(dialogId),
                onConfirm: () => {
                    if (typed.trim().toLowerCase() !== email.trim().toLowerCase()) {
                        render("That does not match.")
                        return
                    }
                    hideDialog(dialogId)
                    askPassword(email)
                }
            })
        }

        render(null)
    }, [refreshDialog, hideDialog, askPassword])

    const node = target && token ? (
        <OTPDialog
            mode="dialog"
            show
            title="Verify force delete"
            description={`Enter the 6-digit code sent to your email to permanently delete ${target}.`}
            onVerify={handleVerify}
            onResend={handleResend}
            onCancel={() => {
                reset()
                addToast("", "Force delete cancelled.")
            }}
            autoFocus
            length={6}
            resendCooldown={60}
            expirationTime={300}
        />
    ) : null

    return { start, node }
}

export default useForceDeleteUser
